import type { MeasurementSystem } from "@/types/measurement.type";
import type { UnitObject, UnitSymbol } from "@/types/unit.type";

export const InvitationRequestSource = {
  PORTAL: "portal"
} as const;

export type InvitationRequestSource =
  (typeof InvitationRequestSource)[keyof typeof InvitationRequestSource];

export const InvitationTokenState = {
  LOADING: "loading",
  VALID: "valid",
  INVALID: "invalid",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
  ALREADY_ACCEPTED: "already_accepted"
} as const;

export type InvitationTokenState =
  (typeof InvitationTokenState)[keyof typeof InvitationTokenState];

export interface InviteUserCreate {
  first_name: string;
  last_name: string;
  email: string;
  role_id: number;
  phone_number?: string | null;
  currency?: string | null;
  country_code?: string | null;
  partner_code?: string | null;
  company_code?: string | null;
  measurement_system?: MeasurementSystem | null;
  flow_rate_unit?: UnitSymbol | null;
  volume_unit?: UnitSymbol | null;
  weight_unit?: UnitSymbol | null;
  long_distance_unit?: UnitSymbol | null;
  short_distance_unit?: UnitSymbol | null;
  diameter_unit?: UnitSymbol | null;
}

export interface CreateUsersRequestRow {
  row_id: number;
  data: InviteUserCreate;
}

export type CreateUsersRequest = CreateUsersRequestRow[];

export interface CreateUsersFieldError {
  field: string;
  message: string;
}

export interface CreateUsersRequestRowError {
  row_id: number;
  fields: CreateUsersFieldError[];
}

export interface CreateUsersValidationResponse {
  success: false;
  errors: CreateUsersRequestRowError[];
}

export interface CreateUsersValidationDetailResponse {
  detail: CreateUsersValidationResponse;
}

export interface InviteUsersResponse {
  success_user_ids: number[];
  failed_user_ids: number[];
}

export interface ValidateTokenUser {
  email: string;
  first_name: string;
  last_name: string;
  role_name: string;
  phone_number?: string | null;
  partner_name?: string | null;
  company_name?: string | null;
  country?: string | null;
  currency?: string | null;
  measurement_system?: MeasurementSystem | null;
  units?: UnitObject[] | null;
}

export interface DecodedInvitation {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  role: string;
  partner: string;
  company: string;
  country: string;
  currency: string;
  measurement_system: string;
  flow_rate: string;
  volume: string;
  weight: string;
  short_distance: string;
  long_distance: string;
  diameter: string;
}

export interface AcceptInvitationRequest {
  password: string;
  accepted_policy_and_terms: boolean;
  first_name: string;
  last_name: string;
  country: string;
  currency: string;
  measurement_system: string;
  flow_rate: string;
  volume: string;
  weight: string;
  short_distance: string;
  long_distance: string;
  diameter: string;
  phone?: string;
}

export interface UserPortalUpdateSelf {
  password: string;
  has_accepted_privacy_policy: boolean;
  first_name?: string;
  last_name?: string;
  phone?: string;
  country_code?: string;
  currency?: string;
  metric_system?: MeasurementSystem;
  units?: UnitObject[];
}

export interface InvitationTokenErrorDetail {
  detail: string;
}

export interface InvitationDistinctOption {
  id: number;
  name: string;
  partner_id?: number | null;
  code?: string | null;
  partner_code?: string | null;
  company_code?: string | null;
}

export interface InvitationDistinctRole {
  id?: number | null;
  code?: string | null;
  name?: string | null;
}

export interface InvitationRoleOption {
  id: number;
  code: string;
  name?: string | null;
}

export interface InvitationOrganizationOption {
  id: number;
  code: string;
  name: string;
  partner_id?: number | null;
  partner_code?: string | null;
  company_code?: string | null;
}
